import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, Hero, In, List, P, Related, Src, Steps, Table, WhatIs, WorkflowCard } from "@/components/seo/kit";

// /integrations/outlook-to-slack. Plan: gtm/products/clearmakes-inbox/seo/pages/outlook-to-slack.md
// Facts opened on 2026-09-29; sources are linked on the page.

const PATH = "/integrations/outlook-to-slack";

const faqs = [
  {
    q: "Why does my Outlook rule to Slack's email address bounce?",
    a: "Many Microsoft 365 organisations block automatic forwarding to outside addresses. Since 2021 it is off by default for new organisations, and the sender gets a 5.7.520 “Access denied” bounce. Ask your admin, or use a workflow that posts to Slack instead of forwarding.",
  },
  {
    q: "Does it work with a Microsoft 365 work account?",
    a: "Yes, if your organisation lets you connect outside apps. Some admins must approve a new app first; if Microsoft asks for admin approval when you connect Outlook, send the request to your IT admin.",
  },
  {
    q: "Can it post a summary instead of the whole email?",
    a: "Yes. Most people post the sender, the subject and a two-line summary. Say what you want in the sentence, for example “post a two-line summary”, and check the step before you switch it on.",
  },
  {
    q: "Is there a free way to try it?",
    a: "Yes. The free plan covers 3 connected apps, 2 new workflows a month and 100 runs a month. Outlook and Slack use two of the three apps. Starter is $29 a month for 5,000 runs.",
  },
  {
    q: "Can I reply to the email from Slack?",
    a: "Not from inside Slack. The workflow posts to Slack; replies still go from Outlook. A workflow can draft a reply in Outlook for you to check, if you ask for that step.",
  },
];

const OutlookToSlack = () => (
  <>
    <PageMeta
      title="Outlook to Slack Automation: Post Emails to Slack"
      description="Post new Outlook emails to the right Slack channel, summarised, without forwarding rules your admin may block. Set up in one sentence; free plan to try."
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Integrations", path: "/integrations" },
        { name: "Outlook to Slack", path: PATH },
      ]}
    />
    <main>
      <Hero
        eyebrow="Integrations · Outlook and Slack"
        title="Outlook to Slack automation that your admin won't bounce"
        lead="When an important email reaches your Outlook mailbox, post it to the right Slack channel with a short summary. No forwarding rule, so no 5.7.520 bounce."
        secondary={{ label: "See every integration", href: "/integrations" }}
      />

      <WhatIs title="What is the Outlook to Slack workflow in ClearMakes Inbox?">
        It is a ClearMakes Inbox workflow that watches an Outlook mailbox and, when a new email matches what you described, posts it to a Slack
        channel. It reads the mailbox through a connected app, not a forwarding rule. You set it up in one sentence; there is a
        free plan, and paid plans start at $29 a month.
      </WhatIs>

      <Band
        tone="pale"
        title="What the Outlook to Slack workflow does"
        answer="It picks the Outlook emails that matter and posts them where your team already talks. Everything else stays in Outlook."
      >
        <div className="grid gap-5 lg:grid-cols-2">
          <WorkflowCard
            sentence="When a new Outlook email comes from a client domain, post the sender, subject and a two-line summary to #clients."
            steps={["When an email arrives in Outlook", "Check it is from a client", "Summarise it", "Post to #clients in Slack"]}
          />
          <WorkflowCard
            sentence="When someone sounds upset in an email, message the account owner in Slack straight away."
            steps={["When an email arrives in Outlook", "Stop if the tone is calm", "Post a direct message in Slack"]}
          />
        </div>
      </Band>

      <Band
        tone="white"
        title="How to set up Outlook to Slack"
        answer="Connect Outlook and Slack, describe the workflow in a sentence, check the steps, and switch it on. You need an Outlook or Microsoft 365 mailbox and a Slack workspace where you can add apps."
      >
        <Steps
          items={[
            { title: "Create a free ClearMakes account", body: <>Start from <In href="/products/inbox">ClearMakes Inbox</In>.</> },
            { title: "Connect Outlook", body: "Open Apps, find Outlook and choose Connect. Microsoft's sign-in screen shows the permissions. A work account may need your admin to approve the app." },
            { title: "Connect Slack", body: "Do the same for Slack and pick the workspace." },
            { title: "Describe the workflow", body: "In Workflows, type what should happen, for example the client example above." },
            { title: "Check the steps and switch it on", body: "Inbox writes the steps and asks at most one question. Change anything you like, create the workflow and switch it on." },
            { title: "Check the first runs", body: "Each run is listed with its steps and the exact post that went to Slack." },
          ]}
        />
      </Band>

      <Band
        tone="pale"
        title="Other ways to get Outlook email into Slack"
        answer="A forwarding rule to Slack's channel email address is free if two things are true: you pay for Slack, and your Microsoft 365 admin allows forwarding to outside addresses. Otherwise you need a connected app."
      >
        <Table
          caption="Ways to send Outlook emails to Slack"
          head={["Way", "What it costs", "Good at", "Watch out for"]}
          rows={[
            [
              "Outlook rule forwarding to Slack's channel email address",
              "Included in paid Slack plans",
              "Copying every matching email into a channel",
              "Slack's email address is paid plans only; many Microsoft 365 organisations block automatic external forwarding (bounce 5.7.520)",
            ],
            [
              "Zapier (Outlook + Slack)",
              "Free: 100 tasks a month, checks every 15 min. Professional: $29.99 a month for 750 tasks",
              "Many Outlook triggers and Slack actions",
              "Each successful action is a task; reviewers report Outlook triggers that stop firing",
            ],
            [
              "IFTTT",
              "Pro: $2.99 a month",
              "Very cheap for one simple rule",
              "Outlook trigger is polled: every hour on Free, every 5 min on Pro",
            ],
            [
              "ClearMakes Inbox",
              "Free: 3 apps, 100 runs a month. Starter: $29 a month for 5,000 runs",
              "Choosing which emails matter, summaries, delays and wait-for-reply in one workflow",
              "Newer and smaller than Zapier; fewer ready-made templates",
            ],
          ]}
          note={
            <>
              Checked on 29 September 2026:{" "}
              <Src href="https://learn.microsoft.com/en-us/defender-office-365/outbound-spam-policies-external-email-forwarding">Microsoft, control external email forwarding</Src>,{" "}
              <Src href="https://slack.com/help/articles/206819278-Send-emails-to-Slack">Slack, Send emails to Slack</Src>, <Src href="https://zapier.com/pricing">Zapier pricing</Src>,{" "}
              <Src href="https://ifttt.com/plans">IFTTT plans</Src>, and a Zapier community thread,{" "}
              <Src href="https://community.zapier.com/how-do-i-3/new-emails-in-microsoft-outlook-aren-t-triggering-my-zap-9105">“new emails in Microsoft Outlook aren't triggering my Zap”</Src>.
            </>
          }
        />
      </Band>

      <Band
        tone="white"
        title="Limits, and who this is not for"
        answer="If your team lives in Microsoft Teams rather than Slack, use Outlook to Teams instead. If you need every email copied word for word and your admin allows forwarding, a rule is simpler."
        narrow
      >
        <List
          items={[
            <>Teams users: see <In href="/integrations/outlook-to-teams">Outlook to Teams</In>.</>,
            "Not a shared inbox: replies still go from Outlook.",
            "AI checks and summaries can be wrong now and then. Look at the first runs before you rely on them.",
            "Shared mailboxes: connect the account that can open the shared mailbox. Whether a given shared mailbox can be watched depends on its Microsoft 365 set-up.",
          ]}
        />
        <P>
          Your data is encrypted in transit and at rest and never used to train AI models. Read <In href="/security-architecture">security and your data</In>.
        </P>
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/integrations/gmail-to-slack", label: "Gmail to Slack", desc: "The same workflow for Gmail." },
          { href: "/integrations/outlook-to-teams", label: "Outlook to Teams", desc: "Post Outlook emails to a Teams channel." },
          { href: "/outlook-integration", label: "Outlook email automation", desc: "Everything a workflow can do with a new Outlook email." },
          { href: "/guides/forward-email-to-slack-channel", label: "Forward emails to a Slack channel", desc: "Every way to do it, free ones first." },
          { href: "/slack-integration", label: "Slack automation", desc: "Posts and messages your workflows can send." },
          { href: "/pricing", label: "ClearMakes Inbox pricing", desc: "Free, Starter, Growth and Enterprise." },
        ]}
      />

      <Closing title="Post the Outlook emails that matter to Slack." line="Connect Outlook and Slack, type one sentence, and check the steps." />
    </main>
    <Footer />
  </>
);

export default OutlookToSlack;
